import type { EstimateLocationInput } from "./estimator";

export type EstimateAreaOption = {
  id: string;
  label: string;
  detail: string;
  estimateLocation: EstimateLocationInput;
  source: "profile" | "detected" | "city" | "country" | "fallback" | "custom";
  // Present on custom options that resolved to a nearest supported area: the
  // display label of the supported location actually used for the estimate
  // (the option's own label keeps the location the user asked for).
  estimateAreaLabel?: string;
};

// Country code → human-readable name via Intl.DisplayNames. Falls back to
// the ISO code if the runtime doesn't support the API or the code isn't
// recognized. Used so the location pill shows "France" instead of "FR".
function displayCountryName(countryCode: string): string | null {
  try {
    const names = new Intl.DisplayNames(["en"], { type: "region" });
    return names.of(countryCode) ?? null;
  } catch {
    return null;
  }
}

const US_STATE_NAMES: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

const US_STATE_CODES_BY_NAME = Object.entries(US_STATE_NAMES).reduce<
  Record<string, string>
>((acc, [code, name]) => {
  acc[name.toLowerCase()] = code;
  return acc;
}, {});

const ZIP_STATE_RANGES: { code: string; min: number; max: number }[] = [
  { code: "MA", min: 1000, max: 2799 },
  { code: "RI", min: 2800, max: 2999 },
  { code: "NH", min: 3000, max: 3899 },
  { code: "ME", min: 3900, max: 4999 },
  { code: "VT", min: 5000, max: 5999 },
  { code: "CT", min: 6000, max: 6999 },
  { code: "NJ", min: 7000, max: 8999 },
  { code: "NY", min: 10000, max: 14999 },
  { code: "PA", min: 15000, max: 19699 },
  { code: "DE", min: 19700, max: 19999 },
  { code: "DC", min: 20000, max: 20599 },
  { code: "VA", min: 20100, max: 24699 },
  { code: "MD", min: 20600, max: 21999 },
  { code: "NC", min: 27000, max: 28999 },
  { code: "SC", min: 29000, max: 29999 },
  { code: "GA", min: 30000, max: 31999 },
  { code: "FL", min: 32000, max: 34999 },
  { code: "AL", min: 35000, max: 36999 },
  { code: "TN", min: 37000, max: 38599 },
  { code: "MS", min: 38600, max: 39799 },
  { code: "KY", min: 40000, max: 42799 },
  { code: "OH", min: 43000, max: 45999 },
  { code: "IN", min: 46000, max: 47999 },
  { code: "MI", min: 48000, max: 49999 },
  { code: "IA", min: 50000, max: 52999 },
  { code: "WI", min: 53000, max: 54999 },
  { code: "MN", min: 55000, max: 56799 },
  { code: "SD", min: 57000, max: 57999 },
  { code: "ND", min: 58000, max: 58999 },
  { code: "MT", min: 59000, max: 59999 },
  { code: "IL", min: 60000, max: 62999 },
  { code: "MO", min: 63000, max: 65999 },
  { code: "KS", min: 66000, max: 67999 },
  { code: "NE", min: 68000, max: 69999 },
  { code: "LA", min: 70000, max: 71499 },
  { code: "AR", min: 71600, max: 72999 },
  { code: "OK", min: 73000, max: 74999 },
  { code: "TX", min: 75000, max: 79999 },
  { code: "CO", min: 80000, max: 81999 },
  { code: "WY", min: 82000, max: 83199 },
  { code: "ID", min: 83200, max: 83999 },
  { code: "UT", min: 84000, max: 84999 },
  { code: "AZ", min: 85000, max: 86999 },
  { code: "NM", min: 87000, max: 88499 },
  { code: "NV", min: 89000, max: 89999 },
  { code: "CA", min: 90000, max: 96199 },
  { code: "HI", min: 96700, max: 96899 },
  { code: "AK", min: 99500, max: 99999 },
  { code: "OR", min: 97000, max: 97999 },
  { code: "WA", min: 98000, max: 99499 },
];

export const US_CITY_ESTIMATE_AREA_OPTIONS: EstimateAreaOption[] = [
  cityOption("Atlanta", "GA"),
  cityOption("Austin", "TX"),
  cityOption("Boston", "MA"),
  cityOption("Chicago", "IL"),
  cityOption("Dallas", "TX"),
  cityOption("Denver", "CO"),
  cityOption("Houston", "TX"),
  cityOption("Los Angeles", "CA"),
  cityOption("Miami", "FL"),
  cityOption("New York", "NY"),
  cityOption("Philadelphia", "PA"),
  cityOption("Phoenix", "AZ"),
  cityOption("San Diego", "CA"),
  cityOption("San Francisco", "CA"),
  cityOption("Seattle", "WA"),
  cityOption("Washington", "DC"),
];

function cityOption(city: string, stateCode: string): EstimateAreaOption {
  return {
    id: `city-${city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${stateCode.toLowerCase()}`,
    label: city,
    detail: `${stateCode} city estimate`,
    source: "city",
    estimateLocation: {
      countryCode: "US",
      stateCode,
      city,
    },
  };
}

export function normalizeUsStateCode(value?: string | null) {
  if (!value) return null;
  const normalized = value.trim();
  const upper = normalized.toUpperCase();
  if (US_STATE_NAMES[upper]) return upper;
  return US_STATE_CODES_BY_NAME[normalized.toLowerCase()] ?? null;
}

export function getUsStateName(stateCode?: string | null) {
  return stateCode ? US_STATE_NAMES[stateCode] : undefined;
}

// Used when a patient browses a different state on a state-level coverage
// screen. This is a representative city ZIP for display and state inference,
// not a claim about the patient's actual location.
const REPRESENTATIVE_US_ZIP_BY_STATE: Record<string, string> = {
  AL: "35203",
  AK: "99501",
  AZ: "85001",
  AR: "72201",
  CA: "90001",
  CO: "80202",
  CT: "06103",
  DE: "19801",
  DC: "20001",
  FL: "33101",
  GA: "30303",
  HI: "96813",
  ID: "83702",
  IL: "60601",
  IN: "46204",
  IA: "50309",
  KS: "66101",
  KY: "40202",
  LA: "70112",
  ME: "04101",
  MD: "21201",
  MA: "02108",
  MI: "48226",
  MN: "55401",
  MS: "39201",
  MO: "63101",
  MT: "59601",
  NE: "68102",
  NV: "89101",
  NH: "03101",
  NJ: "07102",
  NM: "87102",
  NY: "10001",
  NC: "27601",
  ND: "58501",
  OH: "43215",
  OK: "73102",
  OR: "97204",
  PA: "19102",
  RI: "02903",
  SC: "29201",
  SD: "57104",
  TN: "37219",
  TX: "75201",
  UT: "84101",
  VT: "05401",
  VA: "23219",
  WA: "98101",
  WV: "25301",
  WI: "53202",
  WY: "82001",
};

export function getRepresentativeUsZipCode(stateCode?: string | null) {
  return stateCode
    ? (REPRESENTATIVE_US_ZIP_BY_STATE[stateCode.toUpperCase()] ?? null)
    : null;
}

export function inferUsStateCodeFromZip(zipCode?: string | null) {
  const match = zipCode?.match(/\d{5}/);
  if (!match) return null;
  const value = Number(match[0]);
  return (
    ZIP_STATE_RANGES.find((range) => value >= range.min && value <= range.max)
      ?.code ?? null
  );
}

/**
 * Derive the supported jurisdiction from canonical location inputs.
 *
 * Country and postal code are persisted; state/region is intentionally a
 * derived value until the address API models a country-neutral region field.
 * Unsupported countries return null rather than borrowing a US-only mapping.
 */
export function inferRegionCode(
  countryCode?: string | null,
  postalCode?: string | null,
) {
  const normalizedCountry = countryCode?.trim().toLowerCase();
  const isUnitedStates =
    normalizedCountry === "us" ||
    normalizedCountry === "usa" ||
    normalizedCountry === "united states";

  return isUnitedStates ? inferUsStateCodeFromZip(postalCode) : null;
}

export function buildProfileEstimateAreaOption({
  city,
  state,
  zipCode,
  countryCode = "US",
}: {
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  countryCode?: string | null;
}): EstimateAreaOption | null {
  const rawCountryCode = countryCode?.trim();
  const normalizedCountryCode =
    rawCountryCode?.toLowerCase() === "usa" ||
    rawCountryCode?.toLowerCase() === "united states"
      ? "US"
      : (rawCountryCode?.toUpperCase() ?? "US");

  if (normalizedCountryCode !== "US") {
    const countryName =
      displayCountryName(normalizedCountryCode) ?? normalizedCountryCode;
    return {
      id: "profile-country",
      label: countryName,
      detail: "Profile country",
      source: "profile",
      estimateAreaLabel: countryName,
      estimateLocation: { countryCode: normalizedCountryCode },
    };
  }

  const stateCode =
    normalizeUsStateCode(state) ??
    inferRegionCode(normalizedCountryCode, zipCode);
  if (!stateCode && !zipCode) return null;

  const stateName = getUsStateName(stateCode);
  const label = zipCode
    ? zipCode
    : city && stateCode
      ? `${city}, ${stateCode}`
      : (stateName ?? "Your area");

  return {
    id: "profile",
    label,
    detail: zipCode ? "From profile ZIP code" : "From profile location",
    source: "profile",
    estimateLocation: {
      countryCode: "US",
      stateCode: stateCode ?? undefined,
      city: city ?? undefined,
    },
  };
}

export function buildDetectedEstimateAreaOption({
  city,
  state,
  countryCode = "US",
  latitude,
  longitude,
}: {
  city?: string | null;
  state?: string | null;
  countryCode?: string | null;
  latitude: number;
  longitude: number;
}): EstimateAreaOption {
  const rawCountryCode = countryCode?.trim();
  const normalizedCountryCode =
    rawCountryCode?.toLowerCase() === "usa" ||
    rawCountryCode?.toLowerCase() === "united states"
      ? "US"
      : (rawCountryCode?.toUpperCase() ?? "US");
  const stateCode =
    normalizedCountryCode === "US" ? normalizeUsStateCode(state) : null;
  const label =
    city && stateCode
      ? `${city}, ${stateCode}`
      : stateCode
        ? (getUsStateName(stateCode) ?? stateCode)
        : (city ?? "Current location");

  return {
    id: "detected",
    label,
    detail: "From current location",
    source: "detected",
    estimateLocation: {
      countryCode: normalizedCountryCode,
      stateCode: stateCode ?? undefined,
      city: city ?? undefined,
      latitude,
      longitude,
    },
  };
}

export function buildFallbackEstimateAreaOption(): EstimateAreaOption {
  return {
    id: "fallback",
    label: "Your area",
    detail: "Use current location or search a city",
    source: "fallback",
    estimateLocation: { countryCode: "US" },
  };
}
